import React, { useEffect, useState } from 'react';
import { App, Button, Card, Checkbox, DatePicker, Form, Input, Select, Space, Spin } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import RecruitPeoplePicker from './shared/RecruitPeoplePicker';
import { recruitErrorMessage } from './shared/recruitUi';

/** Assign an assessment to an application — full page (templates stay a master popup). */
const AssessmentFormPage: React.FC = () => {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [appSearch, setAppSearch] = useState('');
  const presetApp = sp.get('application_id') || undefined;

  const { data: templates = [] } = useQuery({ queryKey: hrmsKeys.assessmentTemplates(), queryFn: () => hrmsService.listAssessmentTemplates() });
  const { data: apps, isFetching } = useQuery({
    queryKey: hrmsKeys.applications({ picker: appSearch, asm: 1 }),
    queryFn: () => hrmsService.pageApplications({ page: 1, limit: 20, q: appSearch || undefined, statuses: 'Applied,Screening,Interview,Offer,TalentPool' }),
  });
  const { data: preset } = useQuery({
    queryKey: hrmsKeys.application(presetApp || ''),
    queryFn: () => hrmsService.getApplication(presetApp!),
    enabled: Boolean(presetApp),
  });

  useEffect(() => {
    if (presetApp) form.setFieldValue('application_id', presetApp);
  }, [presetApp, form]);

  const options = [
    ...(preset ? [{ value: preset.id, label: `${preset.application_number} — ${preset.candidate_name} (${preset.job_title || ''})` }] : []),
    ...(apps?.rows || [])
      .filter((a: any) => a.id !== preset?.id)
      .map((a: any) => ({ value: a.id, label: `${a.application_number} — ${a.candidate_name} (${a.job_title || ''})` })),
  ];

  const save = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const created = await hrmsService.assignAssessment({
        ...v,
        scheduled_at: v.scheduled_at ? dayjs(v.scheduled_at).toISOString() : undefined,
      });
      message.success(`Assessment ${created.assessment_number} assigned`);
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.assessments() });
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.application(v.application_id) });
      navigate(`/hr/assessments/${created.id}`);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(recruitErrorMessage(e, 'Assign failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Assign assessment"
          subtitle="Give a candidate a scored test or evaluation"
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>
                Assign
              </Button>
            </Space>
          }
        />
      }
    >
      <Card title="Assessment" style={{ maxWidth: 720 }}>
        <Form form={form} layout="vertical">
          <Form.Item name="application_id" label="Application" rules={[{ required: true, message: 'Select the application' }]}>
            <Select
              showSearch
              filterOption={false}
              onSearch={setAppSearch}
              notFoundContent={isFetching ? <Spin size="small" /> : 'No open applications'}
              placeholder="Search candidate, job or application number"
              options={options}
            />
          </Form.Item>
          <Form.Item name="template_id" label="Template" rules={[{ required: true, message: 'Select a template' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={templates.filter((t: any) => t.is_active !== false).map((t: any) => ({ value: t.id, label: `${t.template_name} (${t.assessment_type})` }))}
            />
          </Form.Item>
          <Form.Item name="title" label="Title">
            <Input placeholder="Defaults to the template name" />
          </Form.Item>
          <Form.Item name="scheduled_at" label="Scheduled">
            <DatePicker showTime={{ format: 'HH:mm' }} format="DD MMM YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="evaluator_employee_id" label="Evaluator">
            <RecruitPeoplePicker />
          </Form.Item>
          <Form.Item name="notify_candidate" valuePropName="checked" style={{ marginBottom: 0 }}>
            <Checkbox>Email the candidate</Checkbox>
          </Form.Item>
        </Form>
      </Card>
    </WorkspaceLayout>
  );
};

export default AssessmentFormPage;
